/**
 * The rules a sale-results surface renders by.
 *
 * SHARED, not local to the website, because the phone will grow a results
 * screen and these are exactly the rules that must not drift between the two:
 * what counts as Hot, and what a no-sale reason says to a bidder. A second
 * hand-written copy is how the app and the site come to disagree about the same
 * lot. See `memory/feedback_no_mechanical_divergence.md`.
 */

/**
 * A lot is Hot above this many bids. Jim, 2026-09-08: "mark a lot 'Hot' with a
 * flame emoji if it has more than 20 bids." Strictly MORE than — a lot with
 * exactly 20 is not hot.
 */
export const HOT_BID_THRESHOLD = 20;

/**
 * 🔴 HOT IS DERIVED FROM THE BID COUNT, so it must never be shown when the
 * house hides bid counts: "Hot" is "more than 20 bids" stated in words, and
 * publishing it would leak the number `hide_bid_history` just suppressed.
 * The flag is a required argument rather than an option so a caller has to
 * think about it.
 */
export function isHotLot(bidCount: number, bidCountsHidden: boolean): boolean {
  if (bidCountsHidden) return false;
  return bidCount > HOT_BID_THRESHOLD;
}

/**
 * The two tokens the closing pipeline writes to `lots.no_sale_reason`
 * (`place_bid`, `close_auctions_reconcile_and_isolate`, `proxy_autobid`).
 *
 * 🔴 THE COLUMN HOLDS MACHINE TOKENS, NOT PROSE. Rendering it verbatim put the
 * literal string "reserve_not_met" on a public page.
 */
const NO_SALE_REASONS: Readonly<Record<string, string>> = {
  no_bids: 'No bids',
  reserve_not_met: 'Reserve not met',
};

export interface UnsoldLotFacts {
  noSaleReason: string | null;
  hasReserve: boolean;
  reserveMet: boolean;
  bidCount: number;
}

/**
 * Why a lot did not sell, in words a bidder can read.
 *
 * Reserve-not-met is a NORMAL OUTCOME and the wording keeps it that way — it is
 * never an apology and never rendered in the red that means outbid.
 *
 * An unrecognised token is humanised rather than trusted or dropped: a future
 * writer inventing a third value should not be able to print an identifier at a
 * bidder, and swallowing it would hide the fact that it exists.
 */
export function unsoldReasonLabel(lot: UnsoldLotFacts): string {
  const raw = lot.noSaleReason?.trim() ?? '';
  if (raw !== '') {
    const known = NO_SALE_REASONS[raw];
    if (known !== undefined) return known;
    const words = raw.replace(/_/g, ' ');
    return words.charAt(0).toUpperCase() + words.slice(1);
  }
  if (lot.hasReserve && !lot.reserveMet) return 'Reserve not met';
  if (lot.bidCount === 0) return 'No bids';
  return 'Unsold';
}

/**
 * Basis points as a percentage a person would say out loud: 1300 → "13%",
 * 1250 → "12.5%". Trailing zeros are trimmed because "12.50%" reads like a
 * precision the house did not claim.
 */
export function bpsLabel(bps: number): string {
  const pct = bps / 100;
  if (Number.isInteger(pct)) return `${String(pct)}%`;
  return `${pct.toFixed(2).replace(/0+$/, '').replace(/\.$/, '')}%`;
}

/**
 * Whole minutes where the seconds divide, the seconds themselves where they do
 * not. An anti-snipe rule stated as "1.5 minutes" reads worse than "90 seconds",
 * and both are the same stored number.
 */
export function secondsLabel(seconds: number): string {
  if (seconds % 60 === 0 && seconds !== 0) {
    const mins = seconds / 60;
    return `${String(mins)} minute${mins === 1 ? '' : 's'}`;
  }
  return `${String(seconds)} second${seconds === 1 ? '' : 's'}`;
}
